// ./src/hooks/useEmNotifications.ts
//
// NOTIFICATIONS — the bell and the list (ROADMAP A2b #57).
//
// ⚠⚠ READ AND DISMISSED ARE TWO DIFFERENT THINGS, AND THIS HOOK KEEPS THEM APART. Opening the bell
// marks items READ, which drops the badge. It must NOT empty the list: a host who glances at the
// bell has not dealt with anything. Only DISMISS (or the server actioning it) closes an item. Merge
// the two and a glance silently discards the host's outstanding work.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { DEFAULT_PAGE_SIZE } from "@/config";

import { useApp } from "@/contexts/AppContext";
import { usePageVisibilityContext } from "@/contexts/PageVisibilityContext";
import {
  EmNotification,
  EmNotificationFilter,
  EmNotificationService,
  EmNotificationSetting,
} from "@/utils/api/EmNotificationService";
import { SortItem } from "@/utils/api/commonApiTypes";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

/** Per-button spinner keys. ⚠ Keyed `${id}:${action}` — see `isUpdating`. */
export const NOTIFICATION_ACTION = {
  READ: "read",
  DISMISS: "dismiss",
  /**
   * ⚠ Keyed by KIND, not by a row id — the settings screen has no rows.
   *
   * ⚠⚠ AND THERE ARE TWO OF THEM, ONE PER SWITCH. A kind now has a bell switch AND an email
   * switch, and a single `setting` key would spin BOTH whenever either was tapped — the exact
   * failure the `${id}:${action}` rule exists to prevent, one level down: here the "row" is a kind
   * and the "actions" are its channels.
   */
  SETTING_IN_APP: "setting:inApp",
  SETTING_EMAIL: "setting:email",
} as const;

export interface UseEmNotificationsOptions {
  pageSize?: number;
  initialFilters?: Partial<EmNotificationFilter>;
  /** ⚠ The BELL loads on mount; the page loads on demand. Default off so a provider can choose. */
  autoLoad?: boolean;
  /**
   * Keep the badge LIVE — refresh it when the tab becomes active, and slowly while it stays active.
   *
   * ⚠⚠ WITHOUT THIS THE BADGE IS FETCHED ONCE AND FROZEN, which is worse than having no badge: a
   * stale zero is indistinguishable from "nothing new", so the product quietly stops telling you
   * about the thing it exists to tell you about. Found in real use the day it shipped.
   */
  liveBadge?: boolean;
}

/**
 * How often the badge re-checks WHILE THE TAB IS ACTIVE.
 *
 * ⚠⚠ THE DELETED carexpenses BELL POLLED EVERY 60 SECONDS, UNCONDITIONALLY, FOR EVERY SIGNED-IN
 * USER — and that is the part worth not repeating, not polling itself. This is half that rate and
 * **zero while the tab is hidden or unfocused**, which is most of the time; the read is a single
 * count over `user_notification_unread_idx`, a partial index built for exactly this query.
 * ⓘ The cheap, high-value refresh is the FOCUS one below — this interval only covers somebody
 * sitting on one screen while something happens.
 */
const BADGE_REFRESH_MS = 120000;

export interface UseEmNotificationsReturn {
  notifications: EmNotification[];
  unread: number;
  total: number;
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  /** ⚠ An OpResult, never a string — the page renders it through ErrorAlert. */
  lastError: OpResult | null;
  isEmpty: boolean;
  filters: Partial<EmNotificationFilter>;
  list: (filters?: Partial<EmNotificationFilter>, sortBy?: SortItem[]) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  refreshUnread: () => Promise<void>;
  markRead: (ids: string[]) => Promise<boolean>;
  markAllRead: () => Promise<boolean>;
  dismiss: (ids: string[]) => Promise<boolean>;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  clearError: () => void;
  /** Every kind with this person's resolved channels — including kinds they never configured. */
  settings: EmNotificationSetting[];
  settingsLoading: boolean;
  /**
   * ⚠ Whether the FIRST settings read has COME BACK — not the same as "not loading". The read
   * starts when the drawer opens, in an effect that runs after the first paint, so a gate of
   * `settingsLoading && settings.length === 0` leaves one frame with neither spinner nor rows.
   */
  settingsLoaded: boolean;
  loadSettings: () => Promise<void>;
  /** ⚠ One kind at a time, matching the server mutation. Returns false on failure. */
  saveSetting: (
    kind: string,
    patch: { inApp?: boolean | null; emailDigest?: boolean | null },
    actionKey?: string,
  ) => Promise<boolean>;
}

const DEFAULT_FILTERS: Partial<EmNotificationFilter> = {};
const DEFAULT_SORT: SortItem[] = [{ name: "createdAt", order: "DESC" }];

export const useEmNotifications = (options: UseEmNotificationsOptions = {}): UseEmNotificationsReturn => {
  const { pageSize = DEFAULT_PAGE_SIZE, initialFilters = DEFAULT_FILTERS, autoLoad = false, liveBadge = false } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;
  // ⚠ Safe to use the throwing hook: `PageVisibilityProvider` is the OUTERMOST provider in App.tsx,
  // so anything that can reach the notifications provider is already inside it.
  const { isActive } = usePageVisibilityContext();

  const pageSizeRef = useRef(pageSize);
  const lastQueriedFilterRef = useRef<Partial<EmNotificationFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(DEFAULT_SORT);

  // ⚠ A MONOTONIC CALL ID, not a boolean. Two list() calls in flight (a filter change while the
  // first is still running) land out of order often enough to matter, and the loser overwriting the
  // winner shows the host a stale page with no clue that it is stale.
  const listCallIdRef = useRef(0);

  const [notifications, setNotifications] = useState<EmNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState<Partial<EmNotificationFilter>>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingKeys, setUpdatingKeys] = useState<Set<string>>(new Set());
  const [settings, setSettings] = useState<EmNotificationSetting[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(() => isLoaded && notifications.length === 0, [isLoaded, notifications.length]);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  /**
   * ⚠⚠ KEYED `${id}:${action}`, NOT JUST `${id}`. With the id alone, pressing Dismiss on a row spins
   * that row's Mark-read button too — the two are independent actions on the same record.
   * Both args → that one button's `loading`. Id only → any action on the row, which drives its
   * siblings' `disabled`. Neither → anything at all, for the bulk bar.
   */
  const isUpdating = useCallback(
    (id?: string, actionKey?: string): boolean => {
      if (id && actionKey) {
        return updatingKeys.has(`${id}:${actionKey}`);
      }

      if (id) {
        return [...updatingKeys].some((key) => key.startsWith(`${id}:`));
      }

      return updatingKeys.size > 0;
    },
    [updatingKeys],
  );

  const startUpdating = useCallback((ids: string[], actionKey: string) => {
    setUpdatingKeys((prev) => {
      const next = new Set(prev);

      ids.forEach((id) => next.add(`${id}:${actionKey}`));

      return next;
    });
  }, []);

  const stopUpdating = useCallback((ids: string[], actionKey: string) => {
    setUpdatingKeys((prev) => {
      const next = new Set(prev);

      ids.forEach((id) => next.delete(`${id}:${actionKey}`));

      return next;
    });
  }, []);

  const refreshUnread = useCallback(async () => {
    const result = await EmNotificationService.unreadCount();
    const row = (result.getDataFirst(null) ?? null) as { unread?: number } | null;

    // ⚠ A FAILED COUNT LEAVES THE BADGE ALONE rather than zeroing it. Zero is a claim — "you are all
    // caught up" — and it is the one claim a failed read must never make on the host's behalf.
    if (result.code === OP_RESULT_CODES.OK && row) {
      setUnread(Number(row.unread ?? 0) || 0);
    }
  }, []);

  const list = useCallback(
    async (nextFilters?: Partial<EmNotificationFilter>, sortBy?: SortItem[]) => {
      const callId = ++listCallIdRef.current;
      const useFilters = nextFilters ?? lastQueriedFilterRef.current;
      const useSort = sortBy ?? lastQueriedSortRef.current;

      lastQueriedFilterRef.current = useFilters;
      lastQueriedSortRef.current = useSort;

      setLoading(true);
      // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
      setLoadingMore(false);
      setLastError(null);

      const result = await EmNotificationService.list(useFilters, { page: 1, pageSize: pageSizeRef.current }, useSort);

      if (callId !== listCallIdRef.current) {
        return;
      }

      const rows = (result.getData() ?? []) as EmNotification[];
      const reportedTotal = Number((result as { total?: number }).total ?? rows.length) || 0;

      if (result.code !== OP_RESULT_CODES.OK) {
        // ⚠⚠ THE ERROR IS KEPT AND THE ROWS ARE NOT REPLACED. A failed read that fell through to an
        // empty list would render the first-run empty state — "nothing has happened yet" — over a
        // response we could not read. That is the third empty-looking state, and it is a lie the
        // host cannot do anything about.
        setLastError(result);
        setLoading(false);
        setIsLoaded(true);

        return;
      }

      setNotifications(rows);
      setTotal(reportedTotal);
      setHasMore(rows.length < reportedTotal);
      setCurrentPage(1);
      setFilters(useFilters);
      setIsLoaded(true);
      setLoading(false);
    },
    [],
  );

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const callId = listCallIdRef.current;
    const nextPage = currentPage + 1;

    setLoadingMore(true);

    const result = await EmNotificationService.list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
    );

    // ⚠ A page of the OLD filters must not land on the list a newer read replaced (#114).
    if (callId !== listCallIdRef.current) {
      return;
    }

    if (result.code !== OP_RESULT_CODES.OK) {
      setLastError(result);
      setLoadingMore(false);

      return;
    }

    const rows = (result.getData() ?? []) as EmNotification[];

    const seen = notifications.length + rows.length;

    setNotifications((prev) => [...prev, ...rows.filter((r) => !prev.some((p) => p.id === r.id))]);
    setCurrentPage(nextPage);
    // ⚠ A page that came back EMPTY ends it regardless of what `total` claims — otherwise a total
    // that disagrees with the rows (a row dismissed in another tab between pages) leaves a
    // Load-more button that can never load anything.
    setHasMore(rows.length > 0 && seen < total);
    setLoadingMore(false);
  }, [loading, loadingMore, hasMore, currentPage, notifications.length, total]);

  const refresh = useCallback(async () => {
    await Promise.all([list(), refreshUnread()]);
  }, [list, refreshUnread]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  /**
   * ⚠⚠ MARKING READ PATCHES THE ROWS IN PLACE — IT DOES NOT REMOVE THEM. Read is not dealt with.
   * ⚠ And the badge comes from the SERVER's returned count, not from decrementing locally: the same
   * person may have another tab open, and a locally-decremented badge drifts until a reload.
   */
  const stampLocally = useCallback((ids: string[], field: "readAt" | "dismissedAt") => {
    const stampedAt = new Date().toISOString();
    const idSet = new Set(ids);

    setNotifications((prev) =>
      prev.map((row) => (row.id && idSet.has(row.id) && !row[field] ? { ...row, [field]: stampedAt } : row)),
    );
  }, []);

  const runStamp = useCallback(
    async (ids: string[], actionKey: string, call: () => Promise<OpResult>, field: "readAt" | "dismissedAt") => {
      startUpdating(ids, actionKey);

      const result = await call();

      stopUpdating(ids, actionKey);

      if (result.code !== OP_RESULT_CODES.OK) {
        // ⚠ FAILURE is the only case that surfaces — on success the row visibly changes, which is
        // the confirmation. A success toast on top of a visible change is noise.
        setLastError(result);

        return false;
      }

      const row = (result.getDataFirst(null) ?? null) as { unread?: number } | null;

      stampLocally(ids, field);

      if (row && row.unread != null) {
        setUnread(Number(row.unread) || 0);
      } else {
        await refreshUnread();
      }

      return true;
    },
    [startUpdating, stopUpdating, stampLocally, refreshUnread],
  );

  const markRead = useCallback(
    async (ids: string[]) => runStamp(ids, NOTIFICATION_ACTION.READ, () => EmNotificationService.markRead(ids), "readAt"),
    [runStamp],
  );

  /**
   * ⚠ NO IDS AT ALL — the server reads that as "all of mine", which is what the button means. It is
   * safe because the server pins the recipient on the UPDATE itself.
   * ⚠ The local patch stamps only what is LOADED; the server changed more than that, so the badge
   * comes back from the server rather than being computed here.
   */
  const markAllRead = useCallback(async () => {
    const loadedIds = notifications.filter((n) => !n.readAt).map((n) => n.id!).filter(Boolean);

    return runStamp(loadedIds, NOTIFICATION_ACTION.READ, () => EmNotificationService.markRead(), "readAt");
  }, [notifications, runStamp]);

  const dismiss = useCallback(
    async (ids: string[]) => {
      const ok = await runStamp(ids, NOTIFICATION_ACTION.DISMISS, () => EmNotificationService.dismiss(ids), "dismissedAt");

      if (ok) {
        // ⚠ DISMISS REMOVES IT FROM THE LIST — unlike read. Its disappearance IS the confirmation,
        // which is why there is no toast.
        const idSet = new Set(ids);

        setNotifications((prev) => prev.filter((row) => !(row.id && idSet.has(row.id))));
        setTotal((prev) => Math.max(0, prev - ids.length));
      }

      return ok;
    },
    [runStamp],
  );

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setLastError(null);

    const result = await EmNotificationService.settings();

    if (result.code !== OP_RESULT_CODES.OK) {
      // ⚠ KEPT, and the rows are NOT replaced. A failed read falling through to an empty list would
      // render a settings screen with no kinds on it — which reads as "there is nothing to
      // configure" rather than "we could not load your settings".
      setLastError(result);
      setSettingsLoading(false);
      // ⚠ On the failure path too — a read that FAILED has still come back, and leaving the flag
      // false would hold the spinner over the error alert that says what went wrong.
      setSettingsLoaded(true);

      return;
    }

    setSettings((result.getData() ?? []) as EmNotificationSetting[]);
    setSettingsLoading(false);
    setSettingsLoaded(true);
  }, []);

  /**
   * ⚠⚠ OPTIMISTIC, THEN RECONCILED FROM THE SERVER'S ANSWER. A switch that waits for a round trip
   * before moving feels broken; a switch that moves and then silently disagrees with what was saved
   * is worse. So it flips immediately, and the row is replaced by what the server actually stored —
   * including `isDefault`, which the client cannot work out for itself.
   * ⚠ ON FAILURE IT FLIPS BACK, because the alternative is a screen that claims a setting the server
   * refused.
   */
  const saveSetting = useCallback(
    async (
      kind: string,
      patch: { inApp?: boolean | null; emailDigest?: boolean | null },
      actionKey: string = NOTIFICATION_ACTION.SETTING_IN_APP,
    ) => {
      const before = settings;

      setUpdatingKeys((prev) => new Set(prev).add(`${kind}:${actionKey}`));
      setSettings((prev) => prev.map((row) => (row.kind === kind ? { ...row, ...patch } : row)));

      const result = await EmNotificationService.saveSetting({ kind, ...patch });

      setUpdatingKeys((prev) => {
        const next = new Set(prev);

        next.delete(`${kind}:${actionKey}`);

        return next;
      });

      if (result.code !== OP_RESULT_CODES.OK) {
        setSettings(before);
        setLastError(result);

        return false;
      }

      const saved = (result.getDataFirst(null) ?? null) as EmNotificationSetting | null;

      if (saved) {
        setSettings((prev) => prev.map((row) => (row.kind === kind ? { ...row, ...saved } : row)));
      }

      return true;
    },
    [settings],
  );

  useEffect(() => {
    if (!autoLoad || !userId) {
      return;
    }

    void refreshUnread();
    // ⚠ The badge alone on mount. The LIST is loaded when somebody opens the bell or the page —
    // fetching a page of rows on every signed-in page load is what the deleted carexpenses bell did,
    // every 60 seconds, for a query that never worked.
  }, [autoLoad, userId, refreshUnread]);

  /**
   * KEEP THE BADGE HONEST.
   *
   * ⚠⚠ A BADGE THAT NEVER REFRESHES IS WORSE THAN NO BADGE. It is right when the page loads and
   * wrong from then on, and a stale zero reads as "nothing needs you" — the exact sentence a host
   * acts on by not looking. The first version of this shipped with only the mount fetch above.
   *
   * ⚠ RE-CHECKING ON **BECOMING ACTIVE** IS THE ONE THAT MATTERS, and it is nearly free: a host
   * applies a sponsor decision in another tab, answers a phone call, or resumes the PWA from the
   * background, and the count is right the moment they look. The interval is the fallback for
   * somebody who simply stays on one screen.
   * ⚠ BOTH ARE GATED ON `isActive` (visible AND focused), so a backgrounded tab costs nothing —
   * which is the half the deleted bell got wrong, not the polling itself.
   */
  useEffect(() => {
    if (!liveBadge || !userId || !isActive) {
      return;
    }

    void refreshUnread();

    const timer = window.setInterval(() => {
      void refreshUnread();
    }, BADGE_REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [liveBadge, userId, isActive, refreshUnread]);

  return {
    notifications,
    unread,
    total,
    loading,
    isLoaded,
    loadingMore,
    hasMore,
    lastError,
    isEmpty,
    filters,
    list,
    loadMore,
    refresh,
    refreshUnread,
    markRead,
    markAllRead,
    dismiss,
    isUpdating,
    clearError,
    settings,
    settingsLoading,
    settingsLoaded,
    loadSettings,
    saveSetting,
  };
};
